import React from 'react';
import {
    Dialog,
    DialogProps,
    DialogContent,
    DialogTitleProps,
    DialogContentProps,
    DialogActionsProps,
} from '@carrotfertility/carotene-core';
import { DialogActionsBar, DialogCloseButton, DialogCloseButtonProps, DialogTitleWithImage } from './blocks';
import { Form, FormOnSubmitHandler, FormProps } from '../forms';
import { FieldValues } from 'react-hook-form';

let id_counter = 0;

export type ShortFormDialogProps<TFieldValues extends FieldValues = FieldValues> = Omit<DialogProps, 'onSubmit'> & {
    /**
     * The slot for your Primary Action. Most cases should be a `Button`
     */
    primaryAction: React.ReactNode;
    /**
     * The slot for your Secondary Action. Most cases should be an outlined, secondary `Button`
     */
    secondaryAction: React.ReactNode;
    /**
     * The slot for your Tertiary Action. Most cases should be a text, secondary `Button` or a `Link`
     */
    tertiaryAction?: React.ReactNode;
    /**
     * The title text of your Dialog
     */
    title: string;
    /**
     * Component slot for your Dialog image. Please use `DialogTitleImage`.
     */
    image?: React.ReactNode | React.ReactNode[];
    /**
     * Same onSubmit handler that `Form` would take
     */
    onSubmit?: FormOnSubmitHandler<TFieldValues>;
    /**
     * Turning this to `true` will remove the embedded form from the Dialog.
     */
    removeForm?: boolean;
    /**
     * Props that allow you to update components you don't have control over. In this case, `slotProps.form` will most likely be your best friend for interacting with the underlying `Form` component
     */
    slotProps?: {
        closeButton?: DialogCloseButtonProps;
        title?: DialogTitleProps;
        content?: DialogContentProps;
        actions?: DialogActionsProps;
        form?: Omit<FormProps<TFieldValues>, 'children' | 'onSubmit'>;
    };
};

export function ShortFormDialog<TFieldValues extends FieldValues = FieldValues>(
    props: ShortFormDialogProps<TFieldValues>,
) {
    const {
        title,
        children,
        removeForm,
        slotProps,
        image,
        primaryAction,
        secondaryAction,
        tertiaryAction,
        onSubmit,
        ...otherProps
    } = props;
    const [ariaId] = React.useState(id_counter++);
    const titleId = `short-form-dialog-${ariaId}-title`;

    const dialogContent = (
        <>
            <DialogContent {...slotProps?.content}>{children}</DialogContent>
            <DialogActionsBar
                {...slotProps?.actions}
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                tertiaryAction={tertiaryAction}
            />
        </>
    );

    return (
        <Dialog aria-labelledby={titleId} {...otherProps}>
            <DialogCloseButton
                onClick={otherProps.onClose as React.MouseEventHandler<HTMLButtonElement>}
                {...slotProps?.closeButton}
            />
            <DialogTitleWithImage id={titleId} image={image} {...slotProps?.title}>
                {title}
            </DialogTitleWithImage>
            {removeForm ? (
                dialogContent
            ) : (
                <Form onSubmit={onSubmit as FormOnSubmitHandler<TFieldValues>} {...slotProps?.form}>
                    {dialogContent}
                </Form>
            )}
        </Dialog>
    );
}
